<script setup lang="ts">
import { ref, computed, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useOpsStore } from '@/stores/ops'
import { useCaltopoStore } from '@/stores/caltopo'
import { useAssignmentsStore } from '@/stores/assignments'
import { useCurrentOpRole } from '@/composables/useCurrentOpRole'
import OverviewStatsRow from '@/components/overview/OverviewStatsRow.vue'
import OverviewDescriptionCard from '@/components/overview/OverviewDescriptionCard.vue'
import OverviewIwiAlert from '@/components/overview/OverviewIwiAlert.vue'
import OverviewPriorityTrafficAlert from '@/components/overview/OverviewPriorityTrafficAlert.vue'
import OverviewResolvedAlerts from '@/components/overview/OverviewResolvedAlerts.vue'
import OverviewSubjectCard from '@/components/overview/OverviewSubjectCard.vue'
import OverviewTeamsCard from '@/components/overview/OverviewTeamsCard.vue'
import OverviewRadioLogCard from '@/components/overview/OverviewRadioLogCard.vue'
import OverviewCommandStaff from '@/components/overview/OverviewCommandStaff.vue'
import OverviewSubjectSummary from '@/components/overview/OverviewSubjectSummary.vue'
import OverviewQuickActions from '@/components/overview/OverviewQuickActions.vue'
import OverviewQRCard from '@/components/overview/OverviewQRCard.vue'
import OverviewCommsCard from '@/components/overview/OverviewCommsCard.vue'
import CalTopoPopover from '@/components/assignments/CalTopoPopover.vue'
import PermissionGuard from '@/components/ui/PermissionGuard.vue'

const route = useRoute()
const incidentId = route.params.id as string

const opsStore = useOpsStore()
const caltopoStore = useCaltopoStore()
const assignmentsStore = useAssignmentsStore()
const { opRole } = useCurrentOpRole()

const caltopoConnected = computed(() => !!opsStore.selectedOp?.caltopo_map_id)

async function handleCaltopoConnected(payload: {
  mapId: string
  mapName: string
  caltopoOpId: string | null
  pollIntervalMs: number
  syncData: unknown
}) {
  const op = opsStore.selectedOp
  if (!op) return
  await opsStore.updateOpCaltopoLink(incidentId, op.op_id, payload.mapId, payload.mapName, payload.caltopoOpId, payload.pollIntervalMs)
}

// Assignments are not subscribed centrally in IncidentLayout
watch(
  () => opsStore.selectedOpId,
  (opId) => {
    if (opId) assignmentsStore.subscribeToAssignments(incidentId, opId)
  },
  { immediate: true },
)

// Setup banner
const showSetupBanner = ref(false)

onMounted(() => {
  if (localStorage.getItem(`setup-banner-${incidentId}`) === 'show') {
    showSetupBanner.value = true
  }
})

function dismissBanner() {
  localStorage.removeItem(`setup-banner-${incidentId}`)
  showSetupBanner.value = false
}
</script>

<template>
  <div class="h-full overflow-y-auto">
  <div class="p-6">
    <!-- Setup banner -->
    <div
      v-if="showSetupBanner"
      class="mb-5 flex items-start gap-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded px-4 py-3"
    >
      <svg class="w-4 h-4 text-blue-500 shrink-0 mt-0.5" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p class="text-sm text-blue-700 dark:text-blue-300 flex-1">
        Your incident was created. Add command staff, subject info, teams, and comms using the tabs on the left.
      </p>
      <button type="button" class="text-blue-400 hover:text-blue-600 transition-colors shrink-0" @click="dismissBanner">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>

    <!-- Two-column grid (stacks on mobile) -->
    <div class="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-5 items-start">

      <!-- Left column -->
      <div class="min-w-0 flex flex-col gap-5">
        <OverviewStatsRow />
        <OverviewDescriptionCard />
        <OverviewIwiAlert />
        <OverviewPriorityTrafficAlert />
        <OverviewSubjectCard />
        <OverviewTeamsCard />
        <OverviewRadioLogCard />
      </div>

      <!-- Right sidebar -->
      <div class="flex flex-col gap-4">
        <PermissionGuard v-if="opsStore.selectedOp" action="assignment.create" :role="opRole">
          <CalTopoPopover
            :incident-id="incidentId"
            :op-id="opsStore.selectedOp.op_id"
            :op-number="opsStore.selectedOp.op_number"
            :connected="caltopoConnected"
            :map-name="opsStore.selectedOp.caltopo_map_name"
            :full-width="true"
            @connected="handleCaltopoConnected"
            @toggle-pause="caltopoStore.togglePause()"
            @manual-refresh="caltopoStore.manualSync(true)"
          />
        </PermissionGuard>
        <OverviewCommsCard :incident-id="incidentId" />
        <OverviewResolvedAlerts />
        <OverviewQRCard />
      </div>

    </div>
  </div>
  </div>
</template>
